import { InjectionToken } from '@angular/core';
import { UrlMatchResult, UrlSegment } from '@angular/router';

/**
 * Contribución de rutas de un módulo de negocio "piloto" al mecanismo dinámico
 * de rutas del shell (ver `DynamicModuleHostComponent` en `@shared/layout`).
 *
 * En vez de declarar sus rutas en `app.routes.ts`, un módulo de negocio registra
 * una lista de `ModuleRouteContribution` vía `MODULE_ROUTE_CONTRIBUTIONS` (token
 * `multi: true`) desde su propio `provide*Routes()` en `app.config.ts`. El shell
 * cuelga UNA sola ruta comodín (`path: '**'`) bajo el layout que resuelve, en
 * runtime, cuál contribución debe renderizar según la URL actual.
 */
export interface ModuleRouteContribution {
  /** Prefijo de URL exacto sembrado en el catálogo de módulos de BD (ej. '/tareas'). */
  urlPrefix: string;

  /**
   * Predicado opcional para decidir si esta contribución debe manejar la URL actual.
   * Por defecto (si se omite): igualdad exacta con `urlPrefix` o `url.startsWith(urlPrefix + '/')`.
   */
  matches?: (url: string) => boolean;

  /** Carga perezosa del componente a renderizar (mismo patrón que `loadComponent` de Angular Router). */
  loadComponent: () => Promise<unknown>;

  /** Clave i18n (`titles.*`) del título de pestaña/breadcrumb para esta URL exacta. */
  title: string;
}

/**
 * Token multi-provider: cada módulo de negocio aporta un array de `ModuleRouteContribution`.
 * Al inyectarlo con `multi: true` sobre varios providers, Angular entrega un array de arrays
 * (`ModuleRouteContribution[][]`) — aplánalo con `.flat()` donde lo consumas
 * (ver `DynamicModuleHostComponent`).
 */
export const MODULE_ROUTE_CONTRIBUTIONS = new InjectionToken<ModuleRouteContribution[]>(
  'MODULE_ROUTE_CONTRIBUTIONS'
);

/**
 * Registro plano (fuera de la inyección de dependencias) de los `urlPrefix` ya contribuidos.
 * Lo usa `dynamicModuleUrlMatcher` para decidir, en la config de rutas, si el host dinámico debe
 * reclamar una URL — sin esto, un `path: '**'` puro dentro de los hijos de `AppLayoutComponent`
 * reclama CUALQUIER URL no consumida por sus hermanos, incluidas URLs de árboles de rutas
 * completamente ajenos (ej. `/auth/login`), rompiendo su resolución normal. Un `UrlMatcher` corre
 * durante la configuración de rutas, antes de que exista un inyector, por eso este registro es una
 * variable de módulo plana en vez de un servicio.
 */
const registeredPrefixes = new Set<string>();

/** Registra los `urlPrefix` de una lista de contribuciones (llamado por cada `provide*Routes()`). */
export function registerModuleRoutePrefixes(contributions: ModuleRouteContribution[]): void {
  for (const contribution of contributions) {
    registeredPrefixes.add(contribution.urlPrefix);
  }
}

/**
 * `UrlMatcher` del host dinámico: reclama una URL solo si coincide (exacto o por prefijo de
 * segmento) con alguno de los `urlPrefix` ya registrados vía `registerModuleRoutePrefixes`. Deja
 * pasar cualquier otra URL (ej. `/auth/login`, `/404`, typos) para que Angular Router siga
 * intentando otras ramas del árbol de rutas top-level, en vez de absorberlas.
 */
export function dynamicModuleUrlMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  const url = '/' + segments.map(s => s.path).join('/');
  const isMatch = Array.from(registeredPrefixes).some(
    prefix => url === prefix || url.startsWith(prefix + '/')
  );
  return isMatch ? { consumed: segments } : null;
}
